import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { Notification } from '../types'
import { apiService } from '../services/api'

interface NotificationContextData {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  fetchNotifications: () => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  deleteAllNotifications: () => Promise<void>
}

const NotificationContext = createContext<NotificationContextData | undefined>(undefined)

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await apiService.getNotifications({ page: 1, limit: 50 })
      setNotifications(response.data)
      setUnreadCount(response.unreadCount || 0)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.getUnreadNotificationsCount()
      setUnreadCount(response.count)
    } catch (error) {
      // Silently fail if endpoint doesn't exist yet
      if (error instanceof Error && !error.message.includes('404')) {
        console.error('Error fetching unread count:', error)
      }
      setUnreadCount(0)
    }
  }, [])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await apiService.markNotificationAsRead(notificationId)
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        )
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await apiService.markAllNotificationsAsRead()
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      )
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }, [])

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await apiService.deleteNotification(notificationId)
      const notification = notifications.find(n => n.id === notificationId)
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }, [notifications])

  const deleteAllNotifications = useCallback(async () => {
    try {
      await apiService.deleteAllNotifications()
      setNotifications([])
      setUnreadCount(0)
    } catch (error) {
      console.error('Error deleting all notifications:', error)
    }
  }, [])

  // Poll for new notifications every 30 seconds
  // TEMPORARILY DISABLED - Polling is too heavy on backend
  // TODO: Replace with SSE (Server-Sent Events) for real-time notifications
  useEffect(() => {
    // Initial fetch
    fetchNotifications()
    fetchUnreadCount()

    // DISABLED: Polling every 30 seconds
    // const interval = setInterval(() => {
    //   fetchUnreadCount()
    // }, 30000)
    // return () => clearInterval(interval)
  }, [fetchNotifications, fetchUnreadCount])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
